import { FC, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Cancel, Download, Add, Refresh, History, DeleteForever } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { UserContext } from '../../../context';
import {
  getDocumentationFiles,
  getDocumentationFile,
  replaceDocumentationFile,
  deleteFileManagementDocument
} from '../../../fetch';
import { IFileManagementDocument } from '../../../models';
import { FileDrop, Loader, MobileTable, Table } from '../../../components';
import {
  downloadFile,
  formatDate,
  getDownloadFileName,
  isAllowedToEditUnderContract
} from '../../../helpers';
import { FileManagementHistoryModal } from './file-management-history-modal';
import { AddFileModal } from './add-file-modal';

interface IStaticFileManagement {}

export const StaticFileManagement: FC<IStaticFileManagement> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const userContext = useContext(UserContext);
  const { canAccessFileManagement } = useContext(UserContext);
  const hasEditAccess = isAllowedToEditUnderContract(userContext.user);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});
  const [isReplacing, setIsReplacing] = useState<Record<number, boolean>>({});
  const [isShowingDropzone, setIsShowingDropzone] = useState<Record<number, boolean>>({});
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<IFileManagementDocument | null>(
    null
  );
  const [isModalOpen, showModal] = useState<boolean>(false);

  useEffect(() => {
    if (!canAccessFileManagement) {
      enqueueSnackbar(`Not the right roles, redirecting...`, {
        variant: 'info'
      });
      history.push(`/`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessFileManagement]);

  const {
    isLoading: isLoadingDocumentationFiles,
    data: documentationFiles,
    refetch: refetchDocumentationFiles
  } = useQuery<IFileManagementDocument[], Error>(
    ['documentation-files'],
    async () => await getDocumentationFiles(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleDownloadDocument = useCallback(
    async (documentId: number, fileName: string) => {
      setDownloading(prev => ({ ...prev, [documentId]: true }));
      try {
        const res = await getDocumentationFile(documentId);
        downloadFile(res?.data, getDownloadFileName(res) || fileName);
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error downloading document, please try again.`, {
          variant: 'error'
        });
      } finally {
        setDownloading(prev => ({ ...prev, [documentId]: false }));
      }
    },
    [enqueueSnackbar]
  );

  const handleSaveDocument = useCallback(
    async (file: File, documentId: number) => {
      try {
        setIsReplacing(prev => ({ ...prev, [documentId]: true }));

        const formData = new FormData();
        formData.append('Document', file, file.name);
        await replaceDocumentationFile(documentId, formData);
        await refetchDocumentationFiles();

        enqueueSnackbar('Success, document uploaded!', {
          variant: 'success'
        });
        setIsReplacing(prev => ({ ...prev, [documentId]: false }));
        setIsShowingDropzone(prev => ({ ...prev, [documentId]: false }));
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error uploading document, please try again.`, {
          variant: 'error'
        });
        setIsReplacing(prev => ({ ...prev, [documentId]: false }));
      }
    },
    [enqueueSnackbar, refetchDocumentationFiles]
  );

  const handleDelete = async (documentId: number) => {
    const result = window.confirm('Are you sure you want to delete this file?');
    if (result) {
      setDeleting(true);
      try {
        await deleteFileManagementDocument(documentId);
        refetchDocumentationFiles();
        enqueueSnackbar('File Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error, unable to delete file, please try again', {
          variant: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleClose = () => {
    setSelectedDocumentFile(null);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'File Name',
        accessor: 'fileName',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFileManagementDocument } };
        }) => {
          return (
            <Button
              disabled={downloading[original?.fileManagementDocumentId] || isDeleting}
              onClick={() =>
                handleDownloadDocument(
                  original?.fileManagementDocumentId,
                  original?.fileName ?? original?.type
                )
              }
              startIcon={<Download />}
            >
              {original?.fileName ? (
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                >
                  {original?.fileName}
                </Typography>
              ) : (
                <div>No file name available</div>
              )}
            </Button>
          );
        }
      },
      {
        Header: 'Type',
        accessor: 'type',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFileManagementDocument } };
        }) => {
          return <>{original?.type}</>;
        }
      },
      {
        Header: 'Date Uploaded',
        accessor: 'date',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFileManagementDocument } };
        }) => {
          return original?.date ? (
            <Typography>{formatDate(original?.date)}</Typography>
          ) : (
            <Typography>No date available</Typography>
          );
        }
      },
      {
        Header: isMobile ? 'Actions' : '',
        accessor: 'actions',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFileManagementDocument } };
        }) => {
          const isReplacingForDocument = isReplacing[original.fileManagementDocumentId];
          const isDropzoneVisible = isShowingDropzone[original.fileManagementDocumentId];

          const {
            getRootProps,
            getInputProps,
            open,
            isFocused,
            isDragActive,
            isDragAccept,
            isDragReject,
            isFileDialogActive,
            fileRejections,
            rootRef,
            inputRef,
            acceptedFiles
          } = useDropzone({
            accept: {
              'application/pdf': ['.pdf']
            },
            useFsAccessApi: false,
            onDrop: acceptedFiles => {
              if (acceptedFiles.length > 0) {
                const acceptedFile = acceptedFiles[0];
                handleSaveDocument(acceptedFile, original.fileManagementDocumentId);
              } else {
                enqueueSnackbar(
                  `Invalid file type. Please upload one of these image types, .pdf.`,
                  {
                    variant: 'error'
                  }
                );
              }
            }
          });
          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <>
                {isDropzoneVisible ? (
                  <Grid item xs={12}>
                    {isReplacingForDocument ? (
                      <Loader type="inline" position="centered" /> // Loader during file replacement
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          aria-label="hide dropzone"
                          onClick={() =>
                            setIsShowingDropzone(prev => ({
                              ...prev,
                              [original.fileManagementDocumentId]: false
                            }))
                          }
                        >
                          <Cancel />
                        </IconButton>
                        <FileDrop
                          id="File-drop"
                          getRootProps={getRootProps}
                          getInputProps={getInputProps}
                          open={open}
                          isFocused={isFocused}
                          isDragActive={isDragActive}
                          isDragAccept={isDragAccept}
                          isDragReject={isDragReject}
                          isFileDialogActive={isFileDialogActive}
                          fileRejections={fileRejections}
                          rootRef={rootRef}
                          inputRef={inputRef}
                          acceptedFiles={acceptedFiles}
                          title=""
                        >
                          <Typography variant="body1">
                            Drag and drop a file or{' '}
                            <Box
                              component="a"
                              href="/#"
                              sx={{
                                color: theme => theme.palette.primary.main,
                                textDecoration: 'none'
                              }}
                              onClick={e => e.preventDefault()}
                            >
                              browse and pick
                            </Box>{' '}
                          </Typography>
                        </FileDrop>
                      </Box>
                    )}
                  </Grid>
                ) : (
                  <Button
                    disabled={!hasEditAccess || isDeleting}
                    onClick={() =>
                      setIsShowingDropzone(prev => ({
                        ...prev,
                        [original.fileManagementDocumentId]: true
                      }))
                    }
                    variant="text"
                    color="primary"
                    startIcon={<Refresh />}
                  >
                    {isReplacingForDocument ? 'Replacing...' : 'Replace File'}
                  </Button>
                )}
              </>
              <Button
                disabled={isDeleting}
                onClick={() => setSelectedDocumentFile(original)}
                variant="text"
                color="secondary"
                startIcon={<History />}
              >
                History
              </Button>
              <Button
                disabled={isDeleting}
                color="error"
                onClick={() => {
                  handleDelete(original?.fileManagementDocumentId);
                }}
                startIcon={<DeleteForever />}
                variant="text"
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloading,
    isReplacing,
    isShowingDropzone,
    handleDownloadDocument,
    hasEditAccess,
    isMobile,
    handleSaveDocument,
    handleDelete
  ]);

  return (
    <Box mt={1}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          id="create-new-file"
          onClick={() => {
            showModal(true);
          }}
          color="primary"
          variant="contained"
          startIcon={<Add />}
        >
          Add File
        </Button>
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Table
            columns={columns}
            data={documentationFiles ?? []}
            isLoading={isLoadingDocumentationFiles}
            hidePagination
            mobileProps={{
              fields: columns
                .filter(c => c.Header)
                .map(c => ({
                  name: c.Header,
                  accessor: c.accessor,
                  Cell: c?.Cell
                }))
            }}
            ResponsiveComponent={isMobile ? MobileTable : undefined}
            noResultsText="Files Not Uploaded"
          />
          <FileManagementHistoryModal
            fileNames={selectedDocumentFile ? [selectedDocumentFile.fileName] : []}
            documentName={selectedDocumentFile?.type}
            documentId={selectedDocumentFile?.fileManagementDocumentId}
            onClose={handleClose}
          />
        </Grid>
      </Grid>
      <AddFileModal
        open={isModalOpen}
        onClose={() => {
          refetchDocumentationFiles();
          showModal(false);
        }}
      />
    </Box>
  );
};
